import { useCallback, useEffect, useRef, useState } from "react"

import { invalidateJobActivityQueries } from "@/lib/query-client"

import { LIVE_LOADING_STEP } from "../chat-copy"
import {
  AgentApiError,
  createAgentConversation,
  getAgentConversation,
  listAgentConversations,
  listPendingAgentAsks,
  streamAgentConversationMessage,
  type AgentMutationReceipt,
  type AgentConversationMessage,
  type AgentConversationSummary,
  type AgentConversationTurn,
  type AgentStreamProgressEvent,
  type PendingAgentAsk,
} from "../api/agent-conversation-client"
import {
  activeAskFromMessages,
  activeAskToRobin,
  agentMessageToRobin,
  buildTurnForChoiceClick,
  buildTurnForComposerText,
  closeAskInMessages,
  isBackendConversationId,
  labelFromDate,
  mergePendingAsksIntoMessages,
  mergeUniquePendingAsks,
  pendingAskFromFinalResponse,
  reopenAskInMessages,
  suppressAskInMessages,
} from "../utils/conversation-presenter"
import {
  answeredAskIdFromTurn,
  mergeConversationList,
  resolvedChoiceIdFromTurn,
  titleFrom,
  titleFromFiles,
  upsertMessage,
} from "../utils/conversation-list"
import { usePendingAgentAsks } from "./use-pending-agent-asks"
import type {
  RobinConversation,
  RobinFileAttachment,
  RobinMessage,
} from "../robin-types"

const LOCAL_CHAT_ID = "local_new_chat"

/** Cadence for the response text reveal. It preserves Robin's responding state. */
const RESPONSE_TIMING = {
  word: 26,
  afterText: 140,
} as const

type UseRobinConversationStateOptions = {
  onConversationSelected?: (conversationId: string) => void
  onFreshChatSelected?: () => void
  onConversationPersisted?: (conversationId: string) => void
  /**
   * The conversation is gone or not ours (404/403) — typically a shared or stale
   * deep link. Receives the failed id: hydration is async, so by the time this
   * fires the user may have moved on, and the consumer must check it still
   * matches whatever is currently open before acting on it.
   */
  onConversationUnavailable?: (conversationId: string) => void
}

function blankConversation(id = LOCAL_CHAT_ID): RobinConversation {
  return {
    id,
    title: "New chat",
    status: "idle",
    updatedLabel: "now",
    messages: [],
    pending: null,
    activeAsk: null,
  }
}

/**
 * Pending asks are anchored to a visible bot message. Polling can beat a quiet
 * transcript refresh while the backend is finishing narration, so an ask can be
 * real but temporarily impossible to render. Detecting that case lets the hook
 * keep the conversation out of "done" and ask the backend for fresh truth.
 */
function hasMissingPendingAskAnchors(
  messages: RobinMessage[],
  pendingAsks: PendingAgentAsk[],
  suppressedAskIds: ReadonlySet<string>,
) {
  if (pendingAsks.length === 0) return false
  const messageIds = new Set(messages.map((message) => message.id))
  return pendingAsks.some(
    (ask) =>
      !suppressedAskIds.has(ask.askUserToolCallId) &&
      !messageIds.has(ask.botMessageId),
  )
}

/**
 * Live Agentic ATS state machine. The backend owns conversations, transcript,
 * and ask_user lifecycle; this hook owns only UI timing, optimistic messages,
 * stale-poll protection, and the small amount of state Robin needs to feel alive.
 */
export function useRobinConversationState({
  onConversationSelected,
  onFreshChatSelected,
  onConversationPersisted,
  onConversationUnavailable,
}: UseRobinConversationStateOptions = {}) {
  const [conversations, setConversations] = useState<RobinConversation[]>(() => [
    blankConversation(),
  ])
  const [selectedId, setSelectedId] = useState(LOCAL_CHAT_ID)
  const [askMutationVersion, setAskMutationVersion] = useState(0)

  const idSeq = useRef(0)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const alive = useRef(true)
  const optimisticPreviewUrls = useRef<Set<string>>(new Set())
  const loadingConversations = useRef<Set<string>>(new Set())
  const missingAskHydrateTimers = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map())
  const suppressedAskIds = useRef<Set<string>>(new Set())
  const activeAskByConversation = useRef<Map<string, PendingAgentAsk | null>>(
    new Map(),
  )
  const askMutationVersionRef = useRef(0)
  const convRef = useRef(conversations)
  const selectedIdRef = useRef(selectedId)
  convRef.current = conversations
  selectedIdRef.current = selectedId

  const newId = useCallback((prefix: string) => {
    idSeq.current += 1
    return `${prefix}_${idSeq.current}`
  }, [])

  const bumpAskMutationVersion = useCallback(() => {
    askMutationVersionRef.current += 1
    setAskMutationVersion(askMutationVersionRef.current)
    return askMutationVersionRef.current
  }, [])

  useEffect(() => {
    alive.current = true
    const retryTimers = missingAskHydrateTimers.current
    return () => {
      alive.current = false
      timers.current.forEach(clearTimeout)
      timers.current = []
      optimisticPreviewUrls.current.forEach((url) => URL.revokeObjectURL(url))
      optimisticPreviewUrls.current.clear()
      retryTimers.clear()
    }
  }, [])

  const patch = useCallback(
    (convId: string, update: (conv: RobinConversation) => RobinConversation) => {
      setConversations((prev) =>
        prev.map((conv) => (conv.id === convId ? update(conv) : conv)),
      )
    },
    [],
  )

  const setMsg = useCallback(
    (convId: string, msgId: string, fields: Partial<RobinMessage>) => {
      patch(convId, (conv) => ({
        ...conv,
        messages: conv.messages.map((message) =>
          message.id === msgId ? { ...message, ...fields } : message,
        ),
      }))
    },
    [patch],
  )

  const trackMessageLocalPreviews = useCallback((message: RobinMessage) => {
    for (const url of messageLocalPreviewUrls(message)) {
      optimisticPreviewUrls.current.add(url)
    }
  }, [])

  const releaseMessageLocalPreviews = useCallback((message: RobinMessage) => {
    revokeMessageLocalPreviews(message, optimisticPreviewUrls.current)
  }, [])

  const releaseOptimisticMessageLocalPreviews = useCallback(
    (conversationId: string, optimisticMessageId?: string) => {
      if (!optimisticMessageId) return
      const message = convRef.current
        .find((conv) => conv.id === conversationId)
        ?.messages.find((item) => item.id === optimisticMessageId)
      if (message) releaseMessageLocalPreviews(message)
    },
    [releaseMessageLocalPreviews],
  )

  const ensureBackendShell = useCallback((conversationId: string) => {
    setConversations((prev) => {
      if (prev.some((conv) => conv.id === conversationId)) return prev
      return [
        {
          id: conversationId,
          title: "Loading chat",
          status: "working",
          updatedLabel: "now",
          messages: [],
          pending: { steps: [LIVE_LOADING_STEP] },
          hydrated: false,
          hasHistory: true,
          activeAsk: null,
        },
        ...prev,
      ]
    })
  }, [])

  const applyPendingAsks = useCallback(
    (conversationId: string, pendingAsks: PendingAgentAsk[]) => {
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== conversationId) return conv
          const missingAskAnchors = hasMissingPendingAskAnchors(
            conv.messages,
            pendingAsks,
            suppressedAskIds.current,
          )
          const { messages, activeAsk } = mergePendingAsksIntoMessages(
            conversationId,
            conv.messages,
            pendingAsks,
            suppressedAskIds.current,
          )
          activeAskByConversation.current.set(conversationId, activeAsk)
          return {
            ...conv,
            messages,
            activeAsk: activeAskToRobin(activeAsk),
            pending: null,
            status: activeAsk
              ? "needs_you"
              : missingAskAnchors
                ? "working"
                : messages.length
                  ? "done"
                  : "idle",
            hydrated: true,
            hasHistory: true,
          }
        }),
      )
    },
    [],
  )

  const hydrateConversation = useCallback(
    async (
      conversationId: string,
      { force = false, showLoading = true }: { force?: boolean; showLoading?: boolean } = {},
    ) => {
      if (!isBackendConversationId(conversationId)) return
      const current = convRef.current.find((conv) => conv.id === conversationId)
      if (!force && (current?.hydrated || loadingConversations.current.has(conversationId))) {
        return
      }

      loadingConversations.current.add(conversationId)
      ensureBackendShell(conversationId)
      if (showLoading) {
        patch(conversationId, (conv) => ({
          ...conv,
          status: "working",
          pending: conv.messages.length
            ? conv.pending
            : { steps: [LIVE_LOADING_STEP] },
          hasHistory: true,
        }))
      }

      try {
        const [conversationResp, pendingResp] = await Promise.all([
          getAgentConversation(conversationId),
          listPendingAgentAsks(conversationId),
        ])
        if (!alive.current) return

        const baseMessages = conversationResp.messages.map(agentMessageToRobin)
        const missingAskAnchors = hasMissingPendingAskAnchors(
          baseMessages,
          pendingResp.pendingAsks,
          suppressedAskIds.current,
        )
        const { messages, activeAsk } = mergePendingAsksIntoMessages(
          conversationId,
          baseMessages,
          pendingResp.pendingAsks,
          suppressedAskIds.current,
        )
        activeAskByConversation.current.set(conversationId, activeAsk)
        const conversation = conversationResp.conversation
        if (missingAskAnchors) {
          const existingTimer = missingAskHydrateTimers.current.get(conversationId)
          if (!existingTimer) {
            const retryTimer = setTimeout(() => {
              missingAskHydrateTimers.current.delete(conversationId)
              if (alive.current) {
                void hydrateConversation(conversationId, {
                  force: true,
                  showLoading: false,
                })
              }
            }, 1500)
            missingAskHydrateTimers.current.set(conversationId, retryTimer)
            timers.current.push(retryTimer)
          }
        } else {
          const existingTimer = missingAskHydrateTimers.current.get(conversationId)
          if (existingTimer) {
            clearTimeout(existingTimer)
            missingAskHydrateTimers.current.delete(conversationId)
          }
        }

        setConversations((prev) => {
          const nextConversation: RobinConversation = {
            id: conversation.id,
            title: conversation.title || "New chat",
            status: activeAsk
              ? "needs_you"
              : missingAskAnchors
                ? "working"
                : messages.length
                  ? "done"
                  : "idle",
            updatedLabel: labelFromDate(conversation.updated_at, Date.now()),
            messages,
            pending: null,
            hydrated: true,
            hasHistory: true,
            activeAsk: activeAskToRobin(activeAsk),
          }

          if (prev.some((conv) => conv.id === conversation.id)) {
            return prev.map((conv) =>
              conv.id === conversation.id ? nextConversation : conv,
            )
          }
          return [nextConversation, ...prev]
        })
      } catch (error) {
        if (!alive.current) return
        const gone =
          error instanceof AgentApiError &&
          (error.status === 404 || error.status === 403)
        if (gone) {
          // Deep link to a conversation that is deleted, or belongs to another
          // org. `selectFromUrl` fabricated a shell row before hydrating; drop it
          // rather than leaving a dead "Loading chat" entry in the sidebar
          // forever, and let the caller clear the URL.
          setConversations((prev) =>
            prev.filter((conv) => conv.id !== conversationId),
          )
          onConversationUnavailable?.(conversationId)
          return
        }
        console.warn("[agent-conversation] hydrate failed", error)
        patch(conversationId, (conv) => ({
          ...conv,
          status: "failed",
          pending: null,
          hasHistory: true,
        }))
      } finally {
        loadingConversations.current.delete(conversationId)
      }
    },
    [ensureBackendShell, patch, onConversationUnavailable],
  )

  useEffect(() => {
    const controller = new AbortController()

    async function loadConversationList() {
      try {
        const resp = await listAgentConversations(controller.signal)
        if (!alive.current) return
        setConversations((prev) =>
          mergeConversationList(prev, resp.conversations, Date.now()),
        )
        const selected = selectedIdRef.current
        if (isBackendConversationId(selected)) {
          void hydrateConversation(selected)
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn("[agent-conversation] list failed", error)
        }
      }
    }

    void loadConversationList()
    return () => controller.abort()
  }, [hydrateConversation])

  const adoptConversationId = useCallback(
    (currentId: string, backendConversation: AgentConversationSummary) => {
      const nextId = backendConversation.id
      setConversations((prev) => {
        const current = prev.find((conv) => conv.id === currentId)
        if (!current) return prev

        const withoutDuplicate = prev.filter(
          (conv) => conv.id !== nextId || conv.id === currentId,
        )
        return withoutDuplicate.map((conv) =>
          conv.id === currentId
            ? {
                ...conv,
                id: nextId,
                title: backendConversation.title || conv.title,
                updatedLabel: labelFromDate(backendConversation.updated_at, Date.now()),
                hydrated: true,
                hasHistory: true,
              }
            : conv,
        )
      })
      setSelectedId((id) => (id === currentId ? nextId : id))
      if (selectedIdRef.current === currentId) {
        onConversationPersisted?.(nextId)
      }
      return nextId
    },
    [onConversationPersisted],
  )

  const appendProgress = useCallback(
    (conversationId: string, event: AgentStreamProgressEvent) => {
      const label = event.label?.trim()
      if (!label) return
      patch(conversationId, (conv) => {
        const current = conv.pending?.steps ?? []
        const steps = current[current.length - 1] === label ? current : [...current, label]
        return {
          ...conv,
          status: "working",
          pending: { steps },
          updatedLabel: "now",
        }
      })
    },
    [patch],
  )

  const animateFinalBotMessage = useCallback(
    (
      conversationId: string,
      botMessage: AgentConversationMessage,
      pendingAsks: PendingAgentAsk[],
      mutationReceipt: AgentMutationReceipt | null | undefined,
    ) => {
      const demoMessage = agentMessageToRobin(botMessage)
      const text = demoMessage.text ?? ""
      const streamingMessage: RobinMessage = {
        ...demoMessage,
        receipt: mutationReceipt ?? demoMessage.receipt,
        streamChars: text ? 0 : undefined,
      }

      patch(conversationId, (conv) => ({
        ...conv,
        status: "working",
        pending: null,
        updatedLabel: "now",
        messages: upsertMessage(conv.messages, streamingMessage),
        hydrated: true,
        hasHistory: true,
      }))

      let delay = 60
      if (text) {
        let acc = 0
        for (const part of text.split(/(\s+)/)) {
          acc += part.length
          const upto = acc
          const timer = setTimeout(() => {
            if (alive.current) setMsg(conversationId, botMessage.id, { streamChars: upto })
          }, delay)
          timers.current.push(timer)
          delay += RESPONSE_TIMING.word
        }
      }

      const settleTimer = setTimeout(() => {
        if (!alive.current) return
        patch(conversationId, (conv) => {
          const baseMessages = conv.messages.map((message) =>
            message.id === botMessage.id
              ? { ...message, streamChars: undefined }
              : message,
          )
          const { messages, activeAsk } = mergePendingAsksIntoMessages(
            conversationId,
            baseMessages,
            pendingAsks,
            suppressedAskIds.current,
          )
          activeAskByConversation.current.set(conversationId, activeAsk)
          return {
            ...conv,
            messages,
            activeAsk: activeAskToRobin(activeAsk),
            pending: null,
            status: activeAsk ? "needs_you" : "done",
            updatedLabel: "now",
            hydrated: true,
            hasHistory: true,
          }
        })
      }, delay + RESPONSE_TIMING.afterText)
      timers.current.push(settleTimer)
    },
    [patch, setMsg],
  )

  const refreshAfterConflict = useCallback(
    async (conversationId: string) => {
      bumpAskMutationVersion()
      await hydrateConversation(conversationId, { force: true, showLoading: false })
    },
    [bumpAskMutationVersion, hydrateConversation],
  )

  const runStreamTurn = useCallback(
    async (
      conversationId: string,
      turn: AgentConversationTurn,
      optimisticUserMessageId?: string,
      files: File[] = [],
    ) => {
      patch(conversationId, (conv) => ({
        ...conv,
        status: "working",
        pending: { steps: [] },
        updatedLabel: "now",
        hasHistory: true,
      }))

      try {
        const final = await streamAgentConversationMessage(
          conversationId,
          turn,
          {
            files,
            onProgress: (event) => appendProgress(conversationId, event),
          },
        )
        if (!alive.current) return

        // Robin's tools write on the server, past the app's mutation hook, so
        // a turn that changed something refreshes what a job's Today shows.
        if (final.mutationReceipt) void invalidateJobActivityQueries()

        const finalAsk = pendingAskFromFinalResponse(conversationId, final)
        if (finalAsk) bumpAskMutationVersion()

        let pendingAsks: PendingAgentAsk[] = []
        try {
          pendingAsks = (await listPendingAgentAsks(conversationId)).pendingAsks
        } catch (error) {
          console.warn("[agent-conversation] pending asks after final failed", error)
        }
        pendingAsks = mergeUniquePendingAsks([...pendingAsks, finalAsk])

        const answeredAskId = answeredAskIdFromTurn(turn)
        const resolvedChoiceId = resolvedChoiceIdFromTurn(turn)
        if (answeredAskId) {
          suppressedAskIds.current.add(answeredAskId)
          activeAskByConversation.current.set(conversationId, null)
        }

        const userMessage = agentMessageToRobin(final.userMessage)
        patch(conversationId, (conv) => {
          const messages = replaceOptimisticMessage(
            conv.messages,
            userMessage,
            optimisticUserMessageId,
            releaseMessageLocalPreviews,
          )
          return {
            ...conv,
            messages: answeredAskId
              ? closeAskInMessages(messages, answeredAskId, resolvedChoiceId)
              : messages,
            activeAsk: answeredAskId ? null : conv.activeAsk,
            updatedLabel: "now",
            hydrated: true,
            hasHistory: true,
          }
        })

        animateFinalBotMessage(
          conversationId,
          final.botMessage,
          pendingAsks,
          final.mutationReceipt,
        )
      } catch (error) {
        if (!alive.current) return
        if (error instanceof AgentApiError && error.status === 409) {
          releaseOptimisticMessageLocalPreviews(
            conversationId,
            optimisticUserMessageId,
          )
          await refreshAfterConflict(conversationId)
          return
        }
        const answeredAskId = answeredAskIdFromTurn(turn)
        if (answeredAskId) {
          releaseOptimisticMessageLocalPreviews(
            conversationId,
            optimisticUserMessageId,
          )
          bumpAskMutationVersion()
          patch(conversationId, (conv) => {
            // Ask answers are optimistic. If the stream fails before intake
            // accepts the answer, the backend ask is still open, so the user
            // needs the same composer choices back immediately.
            const messagesWithoutOptimisticUser = optimisticUserMessageId
              ? conv.messages.filter((message) => message.id !== optimisticUserMessageId)
              : conv.messages
            const messages = reopenAskInMessages(
              messagesWithoutOptimisticUser,
              answeredAskId,
            )
            const activeAsk = activeAskFromMessages(
              conversationId,
              messages,
              new Date().toISOString(),
            )
            activeAskByConversation.current.set(conversationId, activeAsk)
            return {
              ...conv,
              messages,
              activeAsk: activeAskToRobin(activeAsk),
              status: activeAsk ? "needs_you" : "failed",
              pending: null,
              updatedLabel: "now",
            }
          })
          void hydrateConversation(conversationId, { force: true, showLoading: false })
          return
        }
        console.warn("[agent-conversation] stream failed", error)
        releaseOptimisticMessageLocalPreviews(conversationId, optimisticUserMessageId)
        patch(conversationId, (conv) => ({
          ...conv,
          status: "failed",
          pending: null,
          updatedLabel: "now",
          messages: stripMessageLocalPreviewUrls(
            conv.messages,
            optimisticUserMessageId,
          ),
        }))
      }
    },
    [
      animateFinalBotMessage,
      appendProgress,
      bumpAskMutationVersion,
      hydrateConversation,
      patch,
      releaseMessageLocalPreviews,
      releaseOptimisticMessageLocalPreviews,
      refreshAfterConflict,
    ],
  )

  const getActiveAsk = useCallback((conv: RobinConversation) => {
    if (activeAskByConversation.current.has(conv.id)) {
      return activeAskByConversation.current.get(conv.id) ?? null
    }
    const fallback = activeAskFromMessages(
      conv.id,
      conv.messages,
      new Date().toISOString(),
    )
    activeAskByConversation.current.set(conv.id, fallback)
    return fallback
  }, [])

  const sendMessage = useCallback(
    (convId: string, text: string, files: File[] = []) => {
      const trimmed = text.trim()
      const safeFiles = files.filter((file) => file.size > 0)
      if (!trimmed && safeFiles.length === 0) return

      const conv = convRef.current.find((item) => item.id === convId)
      if (!conv || conv.pending || conv.status === "working") return

      const activeAsk = getActiveAsk(conv)
      const hasFiles = safeFiles.length > 0
      const { turn, suppressAskId } = hasFiles
        ? {
            turn: { kind: "plain_text" as const, body: trimmed },
            suppressAskId: undefined,
          }
        : buildTurnForComposerText(trimmed, activeAsk)
      if (suppressAskId) {
        suppressedAskIds.current.add(suppressAskId)
        activeAskByConversation.current.set(conv.id, null)
        patch(conv.id, (current) => ({
          ...current,
          messages: suppressAskInMessages(current.messages, suppressAskId),
          activeAsk: null,
          status: current.messages.length ? "done" : "idle",
        }))
      }
      if (turn.kind === "ask_user_response") bumpAskMutationVersion()

      const fileAttachments = safeFiles.length
        ? optimisticFileAttachments(safeFiles)
        : []
      const userMessage: RobinMessage = {
        id: newId("msg"),
        role: "user",
        ...(trimmed ? { text: trimmed } : {}),
        ...(fileAttachments.length ? { fileAttachments } : {}),
      }
      trackMessageLocalPreviews(userMessage)
      const conversationTitle = titleFrom(trimmed || titleFromFiles(safeFiles))

      patch(conv.id, (current) => ({
        ...current,
        title: current.messages.length === 0 ? conversationTitle : current.title,
        status: "working",
        pending: { steps: [] },
        updatedLabel: "now",
        messages: [...current.messages, userMessage],
      }))

      if (isBackendConversationId(conv.id)) {
        void runStreamTurn(conv.id, turn, userMessage.id, safeFiles)
        return
      }

      void (async () => {
        try {
          const { conversation } = await createAgentConversation({
            title: conversationTitle,
          })
          if (!alive.current) return
          const backendId = adoptConversationId(conv.id, conversation)
          await runStreamTurn(backendId, turn, userMessage.id, safeFiles)
        } catch (error) {
          if (!alive.current) return
          console.warn("[agent-conversation] create failed", error)
          releaseMessageLocalPreviews(userMessage)
          patch(conv.id, (current) => ({
            ...current,
            status: "failed",
            pending: null,
            updatedLabel: "now",
            messages: stripMessageLocalPreviewUrls(current.messages, userMessage.id),
          }))
        }
      })()
    },
    [
      adoptConversationId,
      bumpAskMutationVersion,
      getActiveAsk,
      newId,
      patch,
      runStreamTurn,
      trackMessageLocalPreviews,
      releaseMessageLocalPreviews,
    ],
  )

  const chooseOption = useCallback(
    (convId: string, attachmentId: string, choiceId: string) => {
      const conv = convRef.current.find((item) => item.id === convId)
      if (!conv || conv.pending || conv.status === "working") return

      const message = conv.messages.find(
        (item) => item.attachment?.type === "choices" && item.attachment.id === attachmentId,
      )
      const attachment = message?.attachment
      if (
        attachment?.type !== "choices" ||
        attachment.readOnly ||
        attachment.resolved ||
        !attachment.live
      ) {
        return
      }

      const choice = attachment.options.find((option) => option.id === choiceId)
      if (!choice) return

      let turn: AgentConversationTurn
      try {
        turn = buildTurnForChoiceClick(attachment.live, choice.id)
      } catch (error) {
        console.warn("[agent-conversation] invalid choice", error)
        return
      }

      activeAskByConversation.current.set(conv.id, null)
      bumpAskMutationVersion()
      const userMessage: RobinMessage = {
        id: newId("msg"),
        role: "user",
        text: choice.name,
      }

      patch(conv.id, (current) => ({
        ...current,
        status: "working",
        pending: { steps: [] },
        updatedLabel: "now",
        activeAsk: null,
        messages: [
          ...current.messages.map((item) =>
            item.attachment?.type === "choices" && item.attachment.id === attachmentId
              ? {
                  ...item,
                  attachment: { ...item.attachment, resolved: choice.id },
                }
              : item,
          ),
          userMessage,
        ],
      }))

      void runStreamTurn(conv.id, turn, userMessage.id)
    },
    [bumpAskMutationVersion, newId, patch, runStreamTurn],
  )

  const ensureBlankChat = useCallback(() => {
    const existing = convRef.current.find(
      (conv) =>
        !isBackendConversationId(conv.id) &&
        conv.messages.length === 0 &&
        !conv.pending,
    )
    if (existing) {
      setSelectedId(existing.id)
      return existing.id
    }

    const id = newId("local")
    setConversations((prev) => [blankConversation(id), ...prev])
    setSelectedId(id)
    return id
  }, [newId])

  const startNewChat = useCallback(() => {
    ensureBlankChat()
    onFreshChatSelected?.()
  }, [ensureBlankChat, onFreshChatSelected])

  const selectConversation = useCallback(
    (id: string) => {
      setSelectedId(id)
      if (isBackendConversationId(id)) {
        void hydrateConversation(id)
      }
      onConversationSelected?.(id)
    },
    [hydrateConversation, onConversationSelected],
  )

  const selectFromUrl = useCallback(
    (id?: string | null) => {
      if (!id) {
        ensureBlankChat()
        return
      }
      if (!isBackendConversationId(id)) {
        ensureBlankChat()
        return
      }

      ensureBackendShell(id)
      setSelectedId(id)
      void hydrateConversation(id)
    },
    [ensureBackendShell, ensureBlankChat, hydrateConversation],
  )

  const selected =
    conversations.find((conversation) => conversation.id === selectedId) ??
    conversations[0]

  const selectedIsPollingCandidate =
    Boolean(selected) &&
    isBackendConversationId(selected.id) &&
    Boolean(selected.hasHistory) &&
    selected.status !== "working" &&
    selected.status !== "failed" &&
    !selected.pending

  const handlePendingAsks = useCallback(
    ({
      conversationId,
      pendingAsks,
      startedMutationVersion,
    }: {
      conversationId: string
      pendingAsks: PendingAgentAsk[]
      startedMutationVersion: number
    }) => {
      if (conversationId !== selectedIdRef.current) return
      if (startedMutationVersion < askMutationVersionRef.current) return
      const conv = convRef.current.find((item) => item.id === conversationId)
      if (conv?.status === "failed") return
      if (
        conv &&
        hasMissingPendingAskAnchors(
          conv.messages,
          pendingAsks,
          suppressedAskIds.current,
        )
      ) {
        applyPendingAsks(conversationId, pendingAsks)
        void hydrateConversation(conversationId, { force: true, showLoading: false })
        return
      }
      applyPendingAsks(conversationId, pendingAsks)
    },
    [applyPendingAsks, hydrateConversation],
  )

  usePendingAgentAsks({
    enabled: selectedIsPollingCandidate,
    conversationId: selected?.id ?? null,
    mutationVersion: askMutationVersion,
    onPendingAsks: handlePendingAsks,
  })

  return {
    conversations,
    selected,
    selectFromUrl,
    selectConversation,
    sendMessage,
    chooseOption,
    startNewChat,
    // Exposed so callers can drop to a blank draft WITHOUT firing
    // `onFreshChatSelected`. Used when clearing a dead `?c=`, where the URL has
    // already been replaced and `startNewChat` would push a second history entry.
    ensureBlankChat,
  }
}

function optimisticFileAttachments(files: File[]): RobinFileAttachment[] {
  const createdAt = new Date().toISOString()
  const sequence = Date.now()
  return files.map((file, index) => ({
    id: `local_${sequence}_${index}_${file.name || "file"}`,
    fileName: file.name || "Attachment",
    mimeType: file.type || "application/octet-stream",
    sizeBytes: file.size,
    documentKind: "unknown",
    confidence: 0,
    extractedTextPreview: "",
    summary: null,
    createdAt,
    localPreviewUrl: URL.createObjectURL(file),
  }))
}

function replaceOptimisticMessage(
  messages: RobinMessage[],
  backendMessage: RobinMessage,
  optimisticId?: string,
  releaseLocalPreviews: (message: RobinMessage) => void = revokeMessageLocalPreviews,
): RobinMessage[] {
  const withoutBackendDuplicate = messages.filter(
    (message) => message.id !== backendMessage.id,
  )
  if (!optimisticId) return [...withoutBackendDuplicate, backendMessage]

  let replaced = false
  const next = withoutBackendDuplicate.map((message) => {
    if (message.id !== optimisticId) return message
    replaced = true
    releaseLocalPreviews(message)
    return backendMessage
  })
  return replaced ? next : [...next, backendMessage]
}

function messageLocalPreviewUrls(message: RobinMessage): string[] {
  return (
    message.fileAttachments
      ?.map((attachment) => attachment.localPreviewUrl)
      .filter((url): url is string => Boolean(url)) ?? []
  )
}

function revokeMessageLocalPreviews(
  message: RobinMessage,
  trackedUrls?: Set<string>,
) {
  for (const attachment of message.fileAttachments ?? []) {
    if (!attachment.localPreviewUrl) continue
    URL.revokeObjectURL(attachment.localPreviewUrl)
    trackedUrls?.delete(attachment.localPreviewUrl)
  }
}

function stripMessageLocalPreviewUrls(
  messages: RobinMessage[],
  optimisticId?: string,
): RobinMessage[] {
  if (!optimisticId) return messages
  return messages.map((message) => {
    if (message.id !== optimisticId || !message.fileAttachments) return message

    let changed = false
    const fileAttachments = message.fileAttachments.map((attachment) => {
      if (!attachment.localPreviewUrl) return attachment
      changed = true
      const next = { ...attachment }
      delete next.localPreviewUrl
      return next
    })

    return changed ? { ...message, fileAttachments } : message
  })
}
